'use client';

import { faCircleXmark, faEye, faEyeSlash } from '@fortawesome/pro-regular-svg-icons';
import { forwardRef, useRef, useState } from 'react';
import { tv } from 'tailwind-variants';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { InputProps } from './input.types';
import { applyMask } from './input-mask.utils';

import { Icon } from '@/components/ui/icon.component';

const input = tv({
  slots: {
    container: [
      'w-full border rounded-lg transition-all outline-none',
      'focus-within:border-gray-500 border-gray-300',
      'flex items-center justify-between',
      'min-h-[3rem] bg-white overflow-hidden py-2 px-4',
    ],
    input: ['w-full h-full outline-none text-gray-700 bg-transparent',
      'text b-left',
      'truncate',
      'bg-clip-text',
      'placeholder:text-gray-500',
    ],
    startIcon: ['text-gray-500'],
    endIcon: ['text-gray-500 hover:cursor-pointer'],
  },

  variants: {
    disabled: {
      true: {
        container: ['bg-gray-100 border-gray-200 text-gray-400'],
        input: ['bg-gray-100', 'text-gray-400', 'cursor-not-allowed'],
      },
    },

    hasError: {
      true: {
        container: ['border-danger-500'],
      },
    },
  },
});

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      inputClassName,
      type = 'text',
      disabled,
      hasError,
      shouldClearValue = true,
      startIcon,
      startIconStyle,
      endIcon,
      endIconStyle,
      cleanValueDataTestId,
      onClickStartIcon,
      onClear,
      onChange = () => {},
      mask,
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLInputElement | null>(null);
    const inputRef = (ref as React.MutableRefObject<HTMLInputElement | null>) ?? internalRef;

    const {
      container,
      input: inputStyle,
      startIcon: startIconTV,
      endIcon: endIconStyleTV,
    } = input({
      disabled,
      hasError,
    });

    const [showPassword, setShowPassword] = useState(false);

    const displayValue = mask && props.value != null
      ? applyMask(String(props.value), mask)
      : (props.value ?? '');

    const hasValue = Boolean(displayValue && String(displayValue).length > 0);

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
      if (mask) {
        const masked = applyMask(e.target.value, mask);
        const syntheticEvent = {
          ...e,
          target: { ...e.target, value: masked, name: e.target.name },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      } else {
        onChange(e);
      }
    }

    function clearValue() {
      const event = {
        target: {
          name: props.name,
          value: '',
        },
      } as React.ChangeEvent<HTMLInputElement>;

      onChange?.(event);
      onClear?.();
    }

    const { value: _value, ...inputMaskProps } = props;

    return (
      <div className={container({ className })} data-testid={DataTestIdEnum.INPUT_CONTAINER}>
        <div className="flex items-center flex-1 gap-3">
          {startIcon && (
            <Icon
              icon={startIcon}
              className={startIconTV({ className: startIconStyle })}
              data-testid={DataTestIdEnum.INPUT_START_ICON}
            />
          )}

          <input
            ref={inputRef}
            value={displayValue}
            type={showPassword ? 'text' : type}
            className={inputStyle({ className: inputClassName })}
            disabled={disabled}
            onChange={handleChange}
            {...inputMaskProps}
          />
        </div>

        {endIcon && (
          <div onClick={onClickStartIcon} data-testid="end-icon">
            <Icon icon={endIcon} className={endIconStyleTV({ className: endIconStyle })} />
          </div>
        )}

        {shouldClearValue && hasValue && !endIcon && !disabled && type !== 'password' && (
          <div onClick={clearValue} data-testid={cleanValueDataTestId ?? DataTestIdEnum.INPUT_CLEAR_BUTTON} className="cursor-pointer">
            <Icon icon={faCircleXmark} className="text-gray-500" data-testid={DataTestIdEnum.INPUT_END_ICON} />
          </div>
        )}

        {type === 'password' && (
          <div
            className="cursor-pointer"
            onClick={() => setShowPassword(!showPassword)}
            data-testid="password-visible"
            aria-label="Toggle password"
          >
            <Icon icon={showPassword ? faEye : faEyeSlash} className="text-gray-500" />
          </div>
        )}

      </div>
    );
  },
);

Input.displayName = 'Input';

export { Input };
